/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { Bert } from 'meteor/themeteorchef:bert';
import {
  assign,
  capitalize,
  cloneDeep,
  filter,
  forEach,
  includes,
  isEmpty,
  isNil,
  isPlainObject,
  map,
  trimEnd
} from 'lodash';
import { Row, Col } from 'react-bootstrap';

import Missions from '../../../api/Missions/Missions';
import * as projectOps from '../../../api/Projects/project-operations';
import Videos from '../../../api/Videos/Videos';

import { isIAS, isGeoMotionVideo } from '../../../modules/checkDeployEnv';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import TimeFilter from '../../atoms/TimeFilter';
import Loading from '../Loading';
import TableList from '../TableList';
import MapContainerProjectItemsList from '../../containers/MapContainerProjectItemsList';

const PER_PAGE = 12;

class ProjectItemsList extends Component {
  state = {
    allSelected: false,
    isCompleteItemHidden: false,
    isTrashOpen: false,
    highlightedItemId: '',
    selectedItems: new Set()
  };

  changeHighlightedItemId = newHighlightedItemId =>
    this.setState({ highlightedItemId: newHighlightedItemId });

  closeTrash = () => this.setState({ isTrashOpen: false });

  handleDuplicate = async (itemType, itemId) => {
    try {
      await methodPromise(`${itemType}.copy`, itemId);
      Bert.alert('Item Duplicated!', 'info');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleHardRemove = async (itemType, itemId) => {
    try {
      const result = await methodPromise(`${itemType}.hardDelete`, itemId);
      if (!isNil(result) && isPlainObject(result)) {
        Bert.alert(
          `Item deleted! ${result.deletedFiles} files and ${result.deletedFolders} folders`,
          'danger'
        );
      } else {
        Bert.alert('Item deleted!', 'danger');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleRestore = async (itemType, itemId) => {
    try {
      await methodPromise(`${itemType}.restore`, itemId);
      Bert.alert('Item Restored!', 'success');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleSelect = (itemName, itemId) =>
    this.setState(prevState => {
      const { totalItemsCount } = this.props;
      const newSelectedItems = cloneDeep(prevState.selectedItems);
      if (!prevState.selectedItems.has(itemId)) newSelectedItems.add(itemId);
      else if (prevState.selectedItems.has(itemId)) newSelectedItems.delete(itemId);
      const newAllSelected =
        newSelectedItems.size === totalItemsCount[itemName] && newSelectedItems.size > 0;
      return { allSelected: newAllSelected, selectedItems: newSelectedItems };
    });

  handleSelectAll = async itemName =>
    this.setState(prevState => {
      const { selectableItems } = this.props;
      const newSelectedItems = cloneDeep(prevState.selectedItems);
      let newAllSelected = true;
      forEach(selectableItems[itemName], ({ _id: itemId }) => {
        if (!prevState.allSelected && !newSelectedItems.has(itemId)) newSelectedItems.add(itemId);
        else if (prevState.allSelected && newSelectedItems.has(itemId))
          newSelectedItems.delete(itemId);
        newAllSelected = !prevState.allSelected;
      });
      return { allSelected: newAllSelected, selectedItems: newSelectedItems };
    });

  handleSoftRemove = async (itemType, itemId) => {
    try {
      await methodPromise(`${itemType}.softDelete`, itemId);
      Bert.alert('Item moved to Trash!', 'warning');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  openTrash = () => this.setState({ isTrashOpen: true });

  toggleDone = async (itemType, itemId, done) => {
    try {
      await methodPromise(`${itemType}.setDone`, itemId, !done);
      if (!done) Bert.alert('Good Job', 'success');
      else Bert.alert('Keep up the work', 'info');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  toggleDisplayCompleted = () =>
    this.setState(prevState => ({ isCompleteItemHidden: !prevState.isCompleteItemHidden }));

  render() {
    const {
      completeMissionCount,
      currentPage,
      deletedItemsCount,
      deletedItemsList,
      deployEnv,
      deployConfig,
      filterOptions,
      hasMap,
      history,
      isFree,
      isBasic,
      isPremium,
      isPublic,
      items,
      loading,
      match,
      project,
      sort,
      totalItemsCount,
      unfilteredItemsCount,
      changePage,
      handleSearch,
      handleSort
    } = this.props;
    const {
      allSelected,
      isCompleteItemHidden,
      isTrashOpen,
      highlightedItemId,
      selectedItems
    } = this.state;

    const itemName = isIAS(deployEnv) ? 'missions' : 'videos';

    const trashProps = {
      deletedItems: deletedItemsList[itemName],
      deletedCount: deletedItemsCount[itemName],
      isTrashOpen,
      itemList: itemName,
      closeTrash: this.closeTrash,
      restoreItem: this.handleRestore,
      deleteItem: this.handleHardRemove
    };

    const tablePageHeaderProps = {};
    switch (true) {
      case isIAS(deployEnv):
        assign(tablePageHeaderProps, {
          completeItemsCount: completeMissionCount,
          hasToggleHideCompletedButton: true,
          isCompleteItemHidden,
          toggleDisplayCompleted: this.toggleDisplayCompleted
        });
        break;

      case isGeoMotionVideo(deployEnv):
        assign(tablePageHeaderProps, {
          filterOptions,
          hasFilterButton: true
        });
        break;

      default:
        console.error('no environment detected');
        break;
    }
    const limitBasic = !isEmpty(deployConfig) && isBasic ? deployConfig.Roles.Basic.VideoLimit : 0;
    const limitFree = !isEmpty(deployConfig) && isFree ? deployConfig.Roles.Free.VideoLimit : 0;
    const limit = limitBasic >= limitFree ? limitBasic : limitFree;

    const isFreeUserAtLimit =
      !isPublic &&
      (isFree || isBasic) &&
      totalItemsCount[itemName] + deletedItemsCount[itemName] >= limit;

    const newItems = [
      {
        isFreeUserAtLimit,
        itemName,
        newItemRoute: `${match.url}/new${capitalize(trimEnd(itemName, 's'))}`
      }
    ];

    assign(tablePageHeaderProps, {
      filterOptions,
      hasFilterButton: true,
      hasImportButton: false,
      hasSearchInput: true,
      isPremium,
      isPublic,
      newItems,
      handleSearch
    });

    let numberOfItemsDisplayed = totalItemsCount[itemName];
    if (isIAS(deployEnv) && isCompleteItemHidden)
      numberOfItemsDisplayed = totalItemsCount[itemName] - completeMissionCount;

    const tableHeaders = [
      {
        name: capitalize(itemName),
        count: numberOfItemsDisplayed,
        sortValue: 'name',
        filteredCount:
          unfilteredItemsCount[itemName] === totalItemsCount[itemName]
            ? ''
            : unfilteredItemsCount[itemName] - totalItemsCount[itemName]
      },
      {
        isHiddenOnXS: true,
        name: 'Last Updated',
        sortValue: 'updatedAt'
      },
      {
        isHiddenOnXS: true,
        name: 'Created',
        sortValue: 'createdAt'
      }
    ];
    const buttonHeader = { isButtonColumn: true };
    const completeHeader = { isButtonColumn: true, name: 'Completed' };
    const typeHeader = { isHiddenOnXS: true, name: 'Type' };
    if (isIAS(deployEnv)) tableHeaders.push(typeHeader, buttonHeader, buttonHeader, completeHeader);
    else tableHeaders.push(buttonHeader);

    const tableHeaderRowProps = {
      allSelected,
      hasSelectAllButton: isGeoMotionVideo(deployEnv),
      hasTrash: !isPublic,
      sort,
      tableHeaders,
      handleSort,
      openTrash: this.openTrash,
      selectAll: () => this.handleSelectAll(itemName)
    };

    const numberOfColumns = isIAS(deployEnv) ? 8 : 6;

    const tableContentProps = {
      completedColumn: isIAS(deployEnv),
      deleteButton: !isPublic,
      duplicateButton: isIAS(deployEnv),
      exportButton: false,
      history,
      isCompleteItemHidden,
      isPublic,
      isTableEmpty: isEmpty(items[itemName]),
      isVideoList: isGeoMotionVideo(deployEnv),
      itemIdentifier: 'id',
      itemList: itemName,
      itemName,
      items: items[itemName],
      loading,
      match,
      numberOfColumns: isPublic ? numberOfColumns - 1 : numberOfColumns,
      openNewTabButton: true,
      selectColumn: isGeoMotionVideo(deployEnv),
      selectedItems,
      completeItem: this.toggleDone,
      hoverItem: this.changeHighlightedItemId,
      selectItem: this.handleSelect,
      softDeleteItem: this.handleSoftRemove
    };

    const tablePaginationProps = {
      currentPage,
      itemsPerPage: PER_PAGE,
      totalCount: numberOfItemsDisplayed,
      changePage
    };

    if (loading) return <Loading />;

    return (
      <Row>
        {hasMap && (
          <Col xs={12} md={6}>
            <MapContainerProjectItemsList
              project={project}
              items={items[itemName]}
              deployEnv={deployEnv}
              filterOptions={filterOptions}
              itemCollectionName={itemName}
              highlightedItemId={highlightedItemId}
              selectedItems={selectedItems}
            />
            <TimeFilter {...filterOptions.time} />
          </Col>
        )}
        <Col xs={12} md={hasMap ? 6 : 12}>
          <TableList
            childProps={{
              trashProps,
              tablePageHeaderProps,
              tableHeaderRowProps,
              tableContentProps,
              tablePaginationProps
            }}
            isFreeUserAtLimit={isFreeUserAtLimit}
            isPaginationOn
            isFree={isFree}
          />
        </Col>
      </Row>
    );
  }
}

ProjectItemsList.defaultProps = {
  hasMap: false,
  isPublic: false,
  handleSearch: () => ''
};

ProjectItemsList.propTypes = {
  project: PropTypes.object.isRequired,
  completeMissionCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  deletedItemsCount: PropTypes.object.isRequired,
  deletedItemsList: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  deployConfig: PropTypes.object.isRequired,
  hasMap: PropTypes.bool,
  filterOptions: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  isBasic: PropTypes.bool.isRequired,
  isFree: PropTypes.bool.isRequired,
  isPremium: PropTypes.bool.isRequired,
  isPublic: PropTypes.bool,
  items: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  selectableItems: PropTypes.object.isRequired,
  sort: PropTypes.object.isRequired,
  totalItemsCount: PropTypes.object.isRequired,
  unfilteredItemsCount: PropTypes.object.isRequired,
  changePage: PropTypes.func.isRequired,
  handleSearch: PropTypes.func,
  handleSort: PropTypes.func.isRequired
};

export default withTracker(
  ({ project, currentPage, deployEnv, filterOptions, isPublic, searchValue, sort }) => {
    const projectId = projectOps.getId(project);
    const missionSub = Meteor.isClient && Meteor.subscribe('missions', projectId);
    let videoSub;
    if (!isPublic) videoSub = Meteor.isClient && Meteor.subscribe('videos', projectId);
    else if (isEmpty(projectId)) videoSub = Meteor.isClient && Meteor.subscribe('videos.dashboard');
    else videoSub = Meteor.isClient && Meteor.subscribe('videos.publicProject', projectId);

    const skip = currentPage * PER_PAGE - PER_PAGE;

    const videoQuery = { deleted: { $eq: 'no' } };
    const missionQuery = { deleted: { $eq: 'no' } };

    const searchRegex = !isEmpty(searchValue) ? new RegExp(searchValue, 'i') : null;
    if (!isNil(searchRegex)) {
      videoQuery.name = searchRegex;
      missionQuery.name = searchRegex;
    }

    const { filterOn: timeFilterOn, dateRangeStart, dateRangeEnd } = filterOptions.time;
    if (timeFilterOn) {
      videoQuery.createdAt = { $gte: dateRangeStart, $lte: `${dateRangeEnd}T23:59:59.999Z` };
      missionQuery.createdAt = { $gte: dateRangeStart, $lte: `${dateRangeEnd}T23:59:59.999Z` };
    }

    const { filterOn: locationFilterOn, boundingBox } = filterOptions.location;
    if (locationFilterOn) {
      videoQuery['latLng0.0'] = { $gte: boundingBox[0][0], $lte: boundingBox[1][0] };
      videoQuery['latLng0.1'] = { $gte: boundingBox[0][1], $lte: boundingBox[1][1] };
      missionQuery['flightPlan.takeOffPoint.geometry.coordinates.0'] = {
        $gte: boundingBox[0][0],
        $lte: boundingBox[1][0]
      };
      missionQuery['flightPlan.takeOffPoint.geometry.coordinates.1'] = {
        $gte: boundingBox[0][1],
        $lte: boundingBox[1][1]
      };
    }

    const typeField = isIAS(deployEnv) ? 'missionType' : 'type';
    const typeFilters = map(
      filter(
        filterOptions,
        ({ name, filterOn }) => !includes(['tags', 'date', 'location'], name) && filterOn
      ),
      ({ value }) => ({ [typeField]: value })
    );

    if (!isEmpty(typeFilters)) {
      videoQuery.$or = typeFilters;
      missionQuery.$or = typeFilters;
    }

    const sortOptions = { limit: PER_PAGE, skip, sort };

    return {
      loading: !missionSub || !missionSub.ready() || !videoSub || !videoSub.ready(),
      items: {
        missions: Missions.find(missionQuery, sortOptions).fetch(),
        videos: Videos.find(videoQuery, sortOptions).fetch()
      },
      deletedItemsList: {
        missions: Missions.find({ deleted: { $ne: 'no' } }).fetch(),
        videos: Videos.find({ deleted: { $ne: 'no' } }).fetch()
      },
      deletedItemsCount: {
        missions: Missions.find({ deleted: { $ne: 'no' } }).count(),
        videos: Videos.find({ deleted: { $ne: 'no' } }).count()
      },
      totalItemsCount: {
        missions: Missions.find(missionQuery).count(),
        videos: Videos.find(videoQuery).count()
      },
      unfilteredItemsCount: {
        missions: Missions.find({ deleted: { $eq: 'no' } }).count(),
        videos: Videos.find({ deleted: { $eq: 'no' } }).count()
      },
      completeMissionCount: Missions.find({ deleted: { $eq: 'no' }, done: { $eq: true } }).count(),
      selectableItems: {
        missions: Missions.find({ deleted: { $eq: 'no' } }, { fields: { _id: 1 } }).fetch(),
        videos: Videos.find({ deleted: { $eq: 'no' } }, { fields: { _id: 1 } }).fetch()
      }
    };
  }
)(ProjectItemsList);
